/**
 * SF-4651: the demo wizard (/demos/new). Four steps: the prospect's web address, the area, the
 * modules the demo shows (SF-4847), then notes and transcripts. "Create demo" starts the build (POST /admin/demo-builds) and opens the
 * build's own page (/demos/:id) straight away, so a reload or a closed tab can always find it
 * again. With no files Eva drafts straight away. With files (brief S14b) the build is created
 * with transcripts_to_follow and waits for them: the files are handed to the build's page, which
 * sends them, shows each file read or refused, and asks Eva to draft (SF-4729).
 */
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCan } from '@refinedev/core';
import { Alert, Button, Card, Result, Space, Spin, Steps } from 'antd';
import { AreaStep } from './components/AreaStep';
import { ModulesStep } from './components/ModulesStep';
import { NotesStep } from './components/NotesStep';
import { handOverTranscripts, type ChosenTranscript } from './transcripts';
import { WebAddressStep } from './components/WebAddressStep';
import {
  createDemoBuildArgs,
  demoErrorMessage,
  useDemoModuleOptions,
  useDemoMutation,
  type DemoBuild,
} from './api/demoBuildsClient';
import { DEFAULT_MAX_SITES, DEFAULT_RADIUS_MILES, demoDetailPath } from './constants';
import {
  checkArea,
  checkModules,
  checkNotes,
  checkWebAddress,
  type AreaAnswer,
  type ModulesAnswer,
} from './validation';

const STEP_TITLES = ['Web address', 'Area', 'Modules', 'Notes and transcripts'];
const MODULES_STEP = 2;

export const DemoWizardPage: React.FC = () => {
  const navigate = useNavigate();
  const { data: can, isLoading: canLoading } = useCan({ resource: 'demos', action: 'create' });

  const [step, setStep] = useState(0);
  const [domain, setDomain] = useState('');
  const [area, setArea] = useState<AreaAnswer>({
    lat: null,
    lng: null,
    radius_miles: DEFAULT_RADIUS_MILES,
    max_sites: DEFAULT_MAX_SITES,
  });
  // Null until the person touches the Modules step: the ticks then start from the list's defaults.
  const [modulesAnswer, setModulesAnswer] = useState<ModulesAnswer | null>(null);
  const [notes, setNotes] = useState('');
  const [transcripts, setTranscripts] = useState<ChosenTranscript[]>([]);
  const [errors, setErrors] = useState<{
    domain?: string;
    area?: string;
    modules?: string;
    notes?: string;
  }>({});
  const [submitError, setSubmitError] = useState<string | null>(null);

  const { mutateAsync: create, isLoading: creating } = useDemoMutation<DemoBuild>();
  const optionsQuery = useDemoModuleOptions(can?.can === true);
  const options = optionsQuery.data?.data?.options;
  const offered = Array.isArray(options) ? options : null;
  const modules: ModulesAnswer = modulesAnswer ?? {
    mode: 'eva_decides',
    keys: (offered ?? []).filter((o) => o.default_on).map((o) => o.key),
  };
  const offeredKeys = offered ? offered.map((o) => o.key) : null;

  if (canLoading) return <Spin />;
  if (can?.can !== true) {
    return (
      <Result status="403" title="403" subTitle="You do not have permission to create demos." />
    );
  }

  const checkStep = (index: number): boolean => {
    if (index === 0) {
      const r = checkWebAddress(domain);
      setErrors((e) => ({ ...e, domain: r.error }));
      if (r.value) setDomain(r.value);
      return !r.error;
    }
    if (index === 1) {
      const r = checkArea(area);
      setErrors((e) => ({ ...e, area: r.error }));
      return !r.error;
    }
    if (index === MODULES_STEP) {
      const r = checkModules(modules, offeredKeys);
      setErrors((e) => ({ ...e, modules: r.error }));
      return !r.error;
    }
    const r = checkNotes(notes);
    setErrors((e) => ({ ...e, notes: r.error }));
    return !r.error;
  };

  const next = () => {
    if (checkStep(step)) setStep(step + 1);
  };

  const submit = async () => {
    if (creating) return;
    // Every step is checked again: a value could have been changed after its step was passed.
    const d = checkWebAddress(domain);
    const a = checkArea(area);
    const m = checkModules(modules, offeredKeys);
    const n = checkNotes(notes);
    if (
      d.value === undefined ||
      a.value === undefined ||
      m.value === undefined ||
      n.value === undefined
    ) {
      setErrors({ domain: d.error, area: a.error, modules: m.error, notes: n.error });
      setStep(d.error ? 0 : a.error ? 1 : m.error ? MODULES_STEP : 3);
      return;
    }
    setSubmitError(null);
    const toSend = transcripts.filter((t) => !t.refusal);
    let build: DemoBuild;
    try {
      const { data } = await create(
        createDemoBuildArgs({
          domain: d.value,
          area: a.value,
          modules: m.value,
          ...(n.value ? { notes: n.value } : {}),
          ...(toSend.length > 0 ? { transcripts_to_follow: true } : {}),
        }),
      );
      build = data;
    } catch (err) {
      setSubmitError(demoErrorMessage(err, 'The demo could not be started. Try again.'));
      return;
    }
    if (toSend.length > 0) handOverTranscripts(build.id, toSend);
    navigate(demoDetailPath(build.id));
  };

  const stepBody = [
    <WebAddressStep key="web" value={domain} error={errors.domain ?? null} onChange={setDomain} />,
    <AreaStep key="area" value={area} error={errors.area ?? null} onChange={setArea} />,
    <ModulesStep
      key="modules"
      value={modules}
      options={offered}
      loading={optionsQuery.isLoading && optionsQuery.fetchStatus !== 'idle'}
      failed={optionsQuery.isError && !offered}
      error={errors.modules ?? null}
      onChange={setModulesAnswer}
    />,
    <NotesStep
      key="notes"
      notes={notes}
      notesError={errors.notes ?? null}
      onNotesChange={setNotes}
      transcripts={transcripts}
      onTranscriptsChange={setTranscripts}
      locked={creating}
    />,
  ][step];

  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      <Card title="New demo">
        <Steps
          current={step}
          items={STEP_TITLES.map((title) => ({ title }))}
          style={{ marginBottom: 24 }}
        />
        {stepBody}
        {submitError && (
          <Alert type="error" showIcon message={submitError} style={{ marginTop: 16 }} />
        )}
        <Space style={{ marginTop: 24 }}>
          {step > 0 && !creating && <Button onClick={() => setStep(step - 1)}>Back</Button>}
          {step < STEP_TITLES.length - 1 && (
            <Button type="primary" onClick={next}>
              Next
            </Button>
          )}
          {step === STEP_TITLES.length - 1 && (
            <Button type="primary" loading={creating} onClick={submit}>
              Create demo
            </Button>
          )}
        </Space>
      </Card>
    </Space>
  );
};
